import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Coins, ShieldCheck, Users, MapPin, Clock, ArrowRight, Bookmark, GitCompare, MessageCircle, RefreshCw, Loader2, Percent, Building2, Pencil, Save, Trash2, Plus, ChevronDown, History, RotateCcw, Plane } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POINTS = [
  { key: "minimum_investment", label: "Minimum Investment", icon: Coins },
  { key: "residency_outcome", label: "Residency Outcome", icon: ShieldCheck },
  { key: "family", label: "Family", icon: Users },
  { key: "physical_presence", label: "Physical Presence", icon: MapPin },
  { key: "typical_pathway", label: "Typical Pathway", icon: Clock },
];

const EDITABLE_KEYS = [
  "name", "flag", "city", "region", "type", "currency", "from_display", "program",
  "objective", "tagline", "accent", "categories", "snapshot", "tax_profile",
  "real_estate", "processing", "live", "workflow", "visa_free_count", "citizenship_note",
];

const inputCls = "w-full rounded-lg border border-amber-300/30 bg-amber-300/[0.06] px-2.5 py-1.5 text-sm text-white outline-none focus:border-amber-300/70";

function TextField({ value, onChange, area, rows = 2, placeholder }) {
  if (area)
    return <textarea data-testid="cms-input" value={value ?? ""} rows={rows} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  return <input data-testid="cms-input" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
}

export default function CountrySheet({ country, admin, onCountryUpdated, autoEditCode, onAutoEditConsumed, onClose, onExplore, onSave, onCompare, onAsk, onRefresh, onViewVisaMap, saved, comparing }) {
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [history, setHistory] = useState(null); // null=hidden, []=loaded

  useEffect(() => { setEditing(false); setHistory(null); }, [country?.code]);

  useEffect(() => {
    if (!country) return;
    const onKey = (e) => { if (e.key === "Escape" && !editing) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [country, editing, onClose]);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(country)));
    setShowAdvanced(false);
    setHistory(null);
    setEditing(true);
  };

  useEffect(() => {
    if (autoEditCode && country && autoEditCode === country.code && !editing) {
      startEdit();
      onAutoEditConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEditCode, country?.code]);

  const cancelEdit = () => { setEditing(false); setDraft(null); setHistory(null); };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/countries/${country.code}/history`);
      setHistory(data);
    } catch { toast.error("Could not load history"); }
  };
  const undoLast = async () => {
    try {
      const { data } = await axios.post(`${API}/countries/${country.code}/undo`);
      onCountryUpdated?.(data);
      setDraft(JSON.parse(JSON.stringify(data)));
      toast.success("Reverted last change");
      await loadHistory();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nothing to undo");
    }
  };

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setSnap = (key, value) => setDraft((d) => ({ ...d, snapshot: { ...d.snapshot, [key]: value } }));
  const setLive = (value) => setDraft((d) => ({ ...d, live: { ...(d.live || {}), summary: value } }));
  const setStep = (i, key, value) => setDraft((d) => {
    const wf = [...(d.workflow || [])];
    wf[i] = { ...wf[i], [key]: value };
    return { ...d, workflow: wf };
  });
  const addStep = () => setDraft((d) => ({
    ...d,
    workflow: [...(d.workflow || []), { id: `step-${Date.now()}`, title: "New step", classification: "optional", branch: null, summary: "" }],
  }));
  const removeStep = (i) => setDraft((d) => ({ ...d, workflow: (d.workflow || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of EDITABLE_KEYS) if (draft[k] !== undefined) payload[k] = draft[k];
      const { data } = await axios.put(`${API}/countries/${country.code}`, payload);
      onCountryUpdated?.(data);
      setEditing(false);
      setDraft(null);
      toast.success("Destination updated");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const doRefresh = async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    await onRefresh(country.code);
    setRefreshing(false);
  };

  const d = editing ? draft : country;

  return (
    <AnimatePresence>
      {country && d && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={editing ? undefined : onClose}
          />
          <motion.div
            data-testid="country-snapshot-bottom-sheet"
            className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-lg rounded-t-3xl border-t border-white/10"
            style={{ background: "rgba(10,14,23,0.98)", boxShadow: "0 -20px 60px rgba(0,0,0,0.6)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            drag={editing ? false : "y"}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => !editing && info.offset.y > 120 && onClose()}
          >
            <div className="flex justify-center pt-3">
              <div className="h-1.5 w-12 rounded-full bg-white/20" />
            </div>

            <div className="max-h-[80vh] overflow-y-auto px-5 pb-6 pt-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-3">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">
                    Residence through Investment
                  </p>
                  {editing ? (
                    <div className="mt-1 space-y-1.5">
                      <div className="flex gap-1.5">
                        <input data-testid="cms-flag-input" value={d.flag ?? ""} onChange={(e) => set("flag", e.target.value)} className={`${inputCls} w-16 text-center text-xl`} />
                        <TextField value={d.name} onChange={(v) => set("name", v)} placeholder="Country name" />
                      </div>
                      <TextField value={d.tagline} onChange={(v) => set("tagline", v)} placeholder="Tagline" />
                    </div>
                  ) : (
                    <>
                      <h2 className="mt-1 flex items-center gap-2 text-2xl font-semibold text-white">
                        <span className="text-3xl">{d.flag}</span>
                        {d.name}
                      </h2>
                      <p className="mt-1 text-sm text-slate-300">{d.tagline}</p>
                    </>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {admin && !editing && (
                    <button
                      data-testid="cms-edit-btn"
                      onClick={startEdit}
                      className="flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-300/10 px-2.5 py-1.5 text-[11px] font-semibold text-amber-200"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                  {!editing && (
                    <button
                      data-testid="btn-close-snapshot-sheet"
                      onClick={onClose}
                      className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* investment banner */}
              <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: "rgba(226,194,117,0.3)", background: "rgba(226,194,117,0.08)" }}>
                <p className="text-xs uppercase tracking-widest text-slate-300">From</p>
                {editing ? (
                  <div className="mt-1 space-y-1.5">
                    <TextField value={d.from_display} onChange={(v) => set("from_display", v)} placeholder="From … price" />
                    <TextField value={d.program} onChange={(v) => set("program", v)} placeholder="Programme name" />
                  </div>
                ) : (
                  <>
                    <p className="font-mono text-2xl font-semibold text-amber-200">{d.from_display}</p>
                    <p className="mt-0.5 text-xs text-slate-300">{d.program}</p>
                  </>
                )}
              </div>

              {/* passport power (citizenship & residency-to-citizenship) */}
              {(d.type === "Citizenship" || d.citizenship_note || editing) && (
                <div data-testid="passport-power-card" className="mt-3 overflow-hidden rounded-2xl border border-sky-400/25 bg-gradient-to-br from-sky-400/[0.10] to-transparent p-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-sky-400/15 text-sky-300"><Plane size={20} /></div>
                    <div className="flex-1">
                      <p className="text-[11px] font-medium uppercase tracking-wider text-sky-300/90">Passport Power</p>
                      {editing ? (
                        <div className="mt-1 flex items-center gap-2">
                          <input data-testid="cms-visafree-input" type="number" value={d.visa_free_count ?? ""} onChange={(e) => set("visa_free_count", e.target.value === "" ? null : parseInt(e.target.value, 10))} className={`${inputCls} w-24`} placeholder="e.g. 156" />
                          <span className="text-xs text-slate-300">visa-free destinations</span>
                        </div>
                      ) : (
                        <p className="flex items-baseline gap-1.5">
                          <span data-testid="passport-power-count" className="text-2xl font-bold text-white">{d.visa_free_count ?? "—"}</span>
                          <span className="text-xs text-slate-300">countries visa-free</span>
                        </p>
                      )}
                    </div>
                    {!editing && d.visa_free_count != null && (
                      <button data-testid="passport-map-btn" onClick={() => onViewVisaMap?.(country)} className="flex shrink-0 items-center gap-1 rounded-full border border-sky-400/40 bg-sky-400/10 px-2.5 py-1.5 text-[11px] font-semibold text-sky-200">
                        <MapPin size={12} /> Map
                      </button>
                    )}
                  </div>
                  {editing ? (
                    <div className="mt-2">
                      <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Citizenship note (for residency routes)</p>
                      <TextField value={d.citizenship_note} onChange={(v) => set("citizenship_note", v)} placeholder="e.g. EU citizenship eligible after 5 years" />
                    </div>
                  ) : (
                    d.citizenship_note && <p className="mt-1.5 text-[12px] text-sky-200/80">{d.citizenship_note}</p>
                  )}
                </div>
              )}

              {/* 5-point snapshot */}
              <div className="mt-4 space-y-2.5">
                {POINTS.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.key} className="flex gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/5 text-amber-300">
                        <Icon size={16} />
                      </div>
                      <div className="flex-1">
                        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{i + 1}. {p.label}</p>
                        {editing ? (
                          <div className="mt-1"><TextField area rows={2} value={d.snapshot?.[p.key]} onChange={(v) => setSnap(p.key, v)} /></div>
                        ) : (
                          <p className="text-sm leading-snug text-slate-200">{d.snapshot?.[p.key]}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* tax + real estate */}
              <div className="mt-3 grid grid-cols-1 gap-2">
                <div data-testid="snapshot-tax" className="flex gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-400/10 text-emerald-300"><Percent size={16} /></div>
                  <div className="flex-1">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Taxation Profile</p>
                    {editing ? <div className="mt-1"><TextField area value={d.tax_profile} onChange={(v) => set("tax_profile", v)} /></div>
                      : <p className="text-sm leading-snug text-slate-200">{d.tax_profile}</p>}
                  </div>
                </div>
                <div className="flex gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/5 text-amber-300"><Building2 size={16} /></div>
                  <div className="flex-1">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Real Estate Rights</p>
                    {editing ? <div className="mt-1"><TextField area value={d.real_estate} onChange={(v) => set("real_estate", v)} /></div>
                      : <p className="text-sm leading-snug text-slate-200">{d.real_estate}</p>}
                  </div>
                </div>
              </div>

              {/* live feed */}
              <div className="mt-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider">
                    {d.live ? (<><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /><span className="text-emerald-400">Live data</span></>)
                      : (<span className="text-slate-400">Indicative data</span>)}
                  </span>
                  {admin && !editing && (
                    <button
                      data-testid="btn-refresh-live" onClick={doRefresh} disabled={refreshing}
                      className="flex items-center gap-1.5 rounded-full border border-emerald-400/30 px-2.5 py-1 text-[11px] font-medium text-emerald-300 transition-colors hover:bg-emerald-400/10 disabled:opacity-60"
                    >
                      {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      {refreshing ? "Fetching…" : "Refresh latest"}
                    </button>
                  )}
                </div>
                {editing ? (
                  <div className="mt-2">
                    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Live data summary</p>
                    <TextField area rows={3} value={d.live?.summary} onChange={setLive} placeholder="Indicative live-data summary shown to visitors" />
                  </div>
                ) : (
                  <>
                    {d.live?.summary && <p className="mt-2 text-[12px] leading-snug text-slate-300">{d.live.summary}</p>}
                    <p className="mt-1.5 text-[10px] text-slate-400">
                      {d.live ? `${d.live.source} · updated ${new Date(d.live.last_reviewed).toLocaleDateString()} · confidence: ${d.live.confidence}`
                        : "General information only — tap Refresh latest to pull the newest indicative figures."}
                    </p>
                  </>
                )}
              </div>

              {/* advanced + workflow — edit mode only */}
              {editing && (
                <div className="mt-4">
                  <button
                    data-testid="cms-advanced-toggle"
                    onClick={() => setShowAdvanced((s) => !s)}
                    className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-medium text-slate-200"
                  >
                    Advanced fields & Journey steps
                    <ChevronDown size={16} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
                  </button>
                  {showAdvanced && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <AdvField label="City"><TextField value={d.city} onChange={(v) => set("city", v)} /></AdvField>
                        <AdvField label="Region"><TextField value={d.region} onChange={(v) => set("region", v)} /></AdvField>
                        <AdvField label="Type">
                          <select data-testid="cms-type-select" value={d.type ?? ""} onChange={(e) => set("type", e.target.value)} className={inputCls}>
                            <option value="Residency">Residency</option>
                            <option value="Citizenship">Citizenship</option>
                          </select>
                        </AdvField>
                        <AdvField label="Currency"><TextField value={d.currency} onChange={(v) => set("currency", v)} /></AdvField>
                        <AdvField label="Processing"><TextField value={d.processing} onChange={(v) => set("processing", v)} /></AdvField>
                        <AdvField label="Accent">
                          <div className="flex gap-1.5">
                            <input type="color" value={d.accent ?? "#E2C275"} onChange={(e) => set("accent", e.target.value)} className="h-9 w-10 rounded-lg border border-white/10 bg-transparent" />
                            <TextField value={d.accent} onChange={(v) => set("accent", v)} />
                          </div>
                        </AdvField>
                      </div>
                      <AdvField label="Objective"><TextField value={d.objective} onChange={(v) => set("objective", v)} /></AdvField>
                      <AdvField label="Categories (comma separated)">
                        <TextField
                          value={(d.categories || []).join(", ")}
                          onChange={(v) => set("categories", v.split(",").map((s) => s.trim()).filter(Boolean))}
                        />
                      </AdvField>

                      <div>
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Journey steps</p>
                        <div className="space-y-2">
                          {(d.workflow || []).map((step, i) => (
                            <div key={step.id || i} data-testid={`cms-step-${i}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5">
                              <div className="flex items-center gap-1.5">
                                <TextField value={step.title} onChange={(v) => setStep(i, "title", v)} placeholder="Step title" />
                                <button data-testid={`cms-step-remove-${i}`} onClick={() => removeStep(i)} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-red-400/30 text-red-300"><Trash2 size={14} /></button>
                              </div>
                              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                                <select value={step.classification ?? "optional"} onChange={(e) => setStep(i, "classification", e.target.value)} className={inputCls}>
                                  <option value="required">required</option>
                                  <option value="conditional">conditional</option>
                                  <option value="optional">optional</option>
                                </select>
                                <select value={step.branch ?? ""} onChange={(e) => setStep(i, "branch", e.target.value || null)} className={inputCls}>
                                  <option value="">no branch</option>
                                  <option value="property">property</option>
                                  <option value="business">business</option>
                                  <option value="family">family</option>
                                </select>
                              </div>
                              <div className="mt-1.5"><TextField area value={step.summary} onChange={(v) => setStep(i, "summary", v)} placeholder="Step summary" /></div>
                            </div>
                          ))}
                          <button data-testid="cms-step-add" onClick={addStep} className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-white/15 py-2 text-xs font-medium text-slate-300"><Plus size={14} /> Add step</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* edit history + undo */}
              {editing && (
                <div className="mt-3">
                  <div className="flex gap-2">
                    <button
                      data-testid="cms-history-btn"
                      onClick={() => (history === null ? loadHistory() : setHistory(null))}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/12 py-2 text-xs font-medium text-slate-200"
                    >
                      <History size={14} /> {history === null ? "View history" : "Hide history"}
                    </button>
                    <button
                      data-testid="cms-undo-btn"
                      onClick={undoLast}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-amber-300/30 bg-amber-300/10 py-2 text-xs font-semibold text-amber-200"
                    >
                      <RotateCcw size={14} /> Undo last change
                    </button>
                  </div>
                  {history !== null && (
                    <div data-testid="cms-history-list" className="mt-2 space-y-1.5">
                      {history.length === 0 ? (
                        <p className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2 text-[11px] text-slate-400">No edits recorded yet.</p>
                      ) : history.map((h) => (
                        <div key={h.id} className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2 text-[11px] text-slate-300">
                          <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span> · {h.editor}
                          <div className="text-slate-400">changed: {h.changed?.length ? h.changed.join(", ") : "—"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* constant travel disclaimer — every snapshot */}
              {!editing && (
                <p data-testid="travel-disclaimer" className="mt-4 text-center text-[11px] leading-snug text-slate-400">
                  For details about travel information, please discuss with your Consultant.
                </p>
              )}

              {/* action row */}
              {editing ? (
                <div className="mt-4 flex gap-2">
                  <button data-testid="cms-cancel-btn" onClick={cancelEdit} disabled={saving} className="flex-1 rounded-2xl border border-white/12 py-3 text-sm font-semibold text-slate-200 disabled:opacity-60">Cancel</button>
                  <button data-testid="cms-save-btn" onClick={save} disabled={saving} className="flex flex-[2] items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3 text-sm font-semibold text-[#07090E] active:scale-[0.98] disabled:opacity-60">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    data-testid="btn-explore-pathway"
                    onClick={() => onExplore(country)}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98]"
                  >
                    Explore This Pathway <ArrowRight size={17} />
                  </button>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <SecondaryBtn testid="btn-save-country" active={saved} onClick={() => onSave(country)} icon={Bookmark} label={saved ? "Saved" : "Save"} />
                    <SecondaryBtn testid="btn-compare-country" active={comparing} onClick={() => onCompare(country)} icon={GitCompare} label="Compare" />
                    <SecondaryBtn testid="btn-ask-adviser" onClick={onAsk} icon={MessageCircle} label="Ask Us" />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function AdvField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function SecondaryBtn({ testid, onClick, icon: Icon, label, active }) {
  return (
    <button
      data-testid={testid} onClick={onClick}
      className="flex flex-col items-center gap-1 rounded-xl border py-2.5 text-xs transition-colors"
      style={{
        borderColor: active ? "rgba(226,194,117,0.5)" : "rgba(255,255,255,0.08)",
        color: active ? "#E2C275" : "#CBD5E1",
        background: active ? "rgba(226,194,117,0.08)" : "transparent",
      }}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}
